import Axios from '@/utils/axios';

// Shift attendance dashboard — reads the frozen snapshots captured per shift
// by nest-driver-service's scheduler endpoint, never live log-book data.
// Dates are business dates LOCAL to each store's timezone (frozen at capture),
// so date-range params need no timezone conversion on the client.

export interface ShiftAttendanceMitra {
  driverID: number;
  driverName: string;
  driverCode: string | null;
  /** Business date of the shift (local to the store's timezone). */
  date: string;
  shiftCode: string;
  /** IANA zone of the store — render clockInAt in this zone, not the viewer's. */
  timezone: string;
  clockInAt: string; // ISO datetime
  status: 'ON_TIME' | 'LATE';
  lateMinutes: number;
}

export interface ShiftAttendanceStore {
  pitstopID: number;
  pitstopName: string | null;
  /** IANA zone the store's shift windows are expressed in. */
  timezone: string;
  clockIn: number;
  onTime: number;
  late: number;
  /** onTime / clockIn, 0 when no clock-ins. */
  rate: number;
}

export interface ShiftAttendanceTotals {
  clockIn: number;
  onTime: number;
  late: number;
  rate: number;
}

export interface ShiftAttendanceSummary {
  startDate: string;
  endDate: string;
  /** Every shift code captured in the range — drives the shift tabs. */
  shiftCodes: string[];
  totals: ShiftAttendanceTotals;
  stores: ShiftAttendanceStore[];
  capturedAt: string | null;
}

export interface ShiftAttendanceRider {
  driverID: number;
  driverName: string;
  driverCode: string | null;
  /** Distinct business dates this rider clocked in on, within the range. */
  daysActive: number;
  clockIn: number;
  onTime: number;
  late: number;
  /** onTime / clockIn, 0 when no clock-ins. */
  rate: number;
  totalLateMinutes: number;
  avgLateMinutes: number;
}

export interface ShiftAttendanceRangeParams {
  startDate?: string; // YYYY-MM-DD, inclusive; defaults to today (WIB)
  endDate?: string; // YYYY-MM-DD, inclusive; defaults to today (WIB)
  shiftCode?: string;
}

const ShiftAttendanceService = {
  /**
   * GET /v1/drivers/log-book/attendance/shift-summary
   * Provider users are scoped server-side to their own provider.
   * Omit shiftCode to aggregate every captured shift in the range.
   */
  getSummary: async (
    params: ShiftAttendanceRangeParams
  ): Promise<ShiftAttendanceSummary> => {
    const response = await Axios.get(
      '/v1/drivers/log-book/attendance/shift-summary',
      { params }
    );
    return response.data?.data ?? response.data;
  },

  /**
   * GET /v1/drivers/log-book/attendance/shift-summary/mitras
   * One store's mitra snapshots for the same range/shift filter — fetched
   * lazily when a store row is expanded.
   */
  getMitras: async (
    params: ShiftAttendanceRangeParams & { pitstopID: number }
  ): Promise<ShiftAttendanceMitra[]> => {
    const response = await Axios.get(
      '/v1/drivers/log-book/attendance/shift-summary/mitras',
      { params }
    );
    return response.data?.data ?? response.data ?? [];
  },

  /**
   * GET /v1/drivers/log-book/attendance/shift-summary/riders
   * Rider punctuality over the same range/shift filter, worst rate first.
   */
  getRiders: async (
    params: ShiftAttendanceRangeParams
  ): Promise<ShiftAttendanceRider[]> => {
    const response = await Axios.get(
      '/v1/drivers/log-book/attendance/shift-summary/riders',
      { params }
    );
    return response.data?.data ?? response.data ?? [];
  },
};

export default ShiftAttendanceService;
